import { useEffect, useState, useCallback, useRef } from "react";
import ReactMarkdown from "react-markdown";
import izenuAPI from "../../services/izenuAPI";

// Editorial → Ajuda: the articles of ajuda.izenu.ao. An article is a list of
// steps, each with markdown text and, optionally, one screenshot or one YouTube
// video. The preview uses the same renderer as the site (react-markdown, no raw
// HTML), so what you see here is what is published.

const HELP_SITE = "https://ajuda.izenu.ao";
const AUDIENCE = { artist: "Para artistas", listener: "Para ouvintes", all: "Todos" };
const EMPTY_STEP = { title: "", body: "", media_type: "none", media_url: "" };
const EMPTY_ARTICLE = { id: 0, category_id: "", title: "", slug: "", summary: "", status: "draft", featured: 0, published_at: null, steps: [{ ...EMPTY_STEP }] };

const youtubeId = (v) => {
    const s = (v || "").trim();
    if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
    const m = s.match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
};

const slugify = (t) => (t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

const errMsg = (e, fallback) => e?.response?.data?.message || fallback;

export default function HelpCenter() {
    const [data, setData] = useState({ categories: [], articles: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null);      // article being edited
    const [showCategories, setShowCategories] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await izenuAPI.get("/admin/help/list.php");
            setData({ categories: data.categories || [], articles: data.articles || [] });
            setError("");
        } catch (e) {
            setError(errMsg(e, "Não foi possível carregar a Ajuda."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openArticle = async (id) => {
        try {
            const { data } = await izenuAPI.get(`/admin/help/get.php?id=${id}`);
            const a = data.article;
            setEditing({ ...a, steps: a.steps.length ? a.steps : [{ ...EMPTY_STEP }] });
        } catch (e) {
            setError(errMsg(e, "Não foi possível abrir o artigo."));
        }
    };

    const newArticle = (categoryId) =>
        setEditing({ ...EMPTY_ARTICLE, category_id: categoryId || data.categories[0]?.id || "", steps: [{ ...EMPTY_STEP }] });

    const move = async (category, index, dir) => {
        const list = data.articles.filter((a) => a.category_id === category.id);
        const j = index + dir;
        if (j < 0 || j >= list.length) return;
        [list[index], list[j]] = [list[j], list[index]];
        const others = data.articles.filter((a) => a.category_id !== category.id);
        const order = [...others, ...list].map((a) => a.id);
        await izenuAPI.post("/admin/help/reorder.php", { articles: order });
        load();
    };

    if (editing) {
        return (
            <ArticleEditor
                initial={editing}
                categories={data.categories}
                onClose={(changed) => { setEditing(null); if (changed) load(); }}
            />
        );
    }

    return (
        <div className="sm:p-6 text-left" style={s.container}>
            <div style={s.headerRow}>
                <div>
                    <h2 style={s.title}>Ajuda</h2>
                    <p style={s.subtitle}>
                        Os artigos de <a href={HELP_SITE} target="_blank" rel="noreferrer" style={s.link}>ajuda.izenu.ao</a>.
                        Cada artigo é uma sequência de passos com texto, captura de ecrã ou vídeo do YouTube.
                        Um rascunho só aparece no site depois de publicado.
                    </p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <button style={s.chipBtn} onClick={() => setShowCategories((v) => !v)}>Categorias</button>
                    <button style={s.primaryBtn} onClick={() => newArticle()}>Novo artigo</button>
                </div>
            </div>

            {error && <div style={s.errorBox}>{error}</div>}
            {showCategories && <CategoriesCard categories={data.categories} articles={data.articles} onChange={load} />}
            {loading && <p style={s.empty}>A carregar…</p>}

            {!loading && data.categories.map((c) => {
                const list = data.articles.filter((a) => a.category_id === c.id);
                return (
                    <div key={c.id} style={s.card}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
                            <h3 style={s.cardTitle}>{c.title}</h3>
                            <span style={s.badge}>{AUDIENCE[c.audience]}</span>
                            <button style={{ ...s.chipBtn, marginLeft: "auto" }} onClick={() => newArticle(c.id)}>+ Artigo</button>
                        </div>
                        {list.length === 0 && <p style={s.emptySmall}>Sem artigos.</p>}
                        {list.map((a, i) => (
                            <div key={a.id} style={s.listItem}>
                                <div style={{ display: "flex", flexDirection: "column" }}>
                                    <button style={s.arrow} disabled={i === 0} onClick={() => move(c, i, -1)}>↑</button>
                                    <button style={s.arrow} disabled={i === list.length - 1} onClick={() => move(c, i, 1)}>↓</button>
                                </div>
                                <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => openArticle(a.id)}>
                                    <div style={s.itemTitle}>
                                        {a.title}
                                        {a.featured === 1 && <span style={{ ...s.badge, marginLeft: 8 }}>Mais procurado</span>}
                                    </div>
                                    <div style={s.itemSub}>/{a.slug} · {a.step_count} passo{a.step_count === 1 ? "" : "s"}</div>
                                </div>
                                <StatusBadge status={a.status} />
                                {a.status === "published" && (
                                    <a href={`${HELP_SITE}/${a.slug}`} target="_blank" rel="noreferrer" style={s.chipLink}>Ver no site</a>
                                )}
                                <button style={s.chipBtn} onClick={() => openArticle(a.id)}>Editar</button>
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

function StatusBadge({ status }) {
    const pub = status === "published";
    return (
        <span style={{ ...s.badge, background: pub ? "#d1fae5" : "#fef3c7", color: pub ? "#065f46" : "#92400e" }}>
            {pub ? "Publicado" : "Rascunho"}
        </span>
    );
}

// ------------------------------------------------------------------ categories

function CategoriesCard({ categories, articles, onChange }) {
    const [form, setForm] = useState({ id: 0, title: "", description: "", audience: "artist" });
    const [error, setError] = useState("");

    const save = async () => {
        try {
            await izenuAPI.post("/admin/help/saveCategory.php", form);
            setForm({ id: 0, title: "", description: "", audience: "artist" });
            setError("");
            onChange();
        } catch (e) { setError(errMsg(e, "Não foi possível gravar.")); }
    };
    const remove = async (c) => {
        if (!window.confirm(`Apagar a categoria "${c.title}"?`)) return;
        try { await izenuAPI.post("/admin/help/deleteCategory.php", { id: c.id }); onChange(); }
        catch (e) { setError(errMsg(e, "Não foi possível apagar.")); }
    };
    const move = async (i, dir) => {
        const list = [...categories];
        const j = i + dir;
        if (j < 0 || j >= list.length) return;
        [list[i], list[j]] = [list[j], list[i]];
        await izenuAPI.post("/admin/help/reorder.php", { categories: list.map((c) => c.id) });
        onChange();
    };

    return (
        <div style={s.card}>
            <h3 style={s.cardTitle}>Categorias</h3>
            <p style={s.cardDesc}>Aparecem na página inicial da Ajuda, agrupadas por público, por esta ordem. Só uma categoria vazia pode ser apagada.</p>
            {error && <div style={s.errorBox}>{error}</div>}
            {categories.map((c, i) => (
                <div key={c.id} style={s.listItem}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <button style={s.arrow} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                        <button style={s.arrow} disabled={i === categories.length - 1} onClick={() => move(i, 1)}>↓</button>
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={s.itemTitle}>{c.title} <span style={s.badge}>{AUDIENCE[c.audience]}</span></div>
                        <div style={s.itemSub}>{c.description || "—"} · {articles.filter((a) => a.category_id === c.id).length} artigos</div>
                    </div>
                    <button style={s.chipBtn} onClick={() => setForm({ id: c.id, title: c.title, description: c.description || "", audience: c.audience })}>Editar</button>
                    <button style={s.dangerBtn} onClick={() => remove(c)}>Apagar</button>
                </div>
            ))}
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 14 }}>
                <div style={{ flex: "1 1 200px" }}>
                    <label style={s.label}>{form.id ? "Editar categoria" : "Nova categoria"}</label>
                    <input style={s.input} placeholder="Nome" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div style={{ flex: "2 1 280px" }}>
                    <label style={s.label}>Descrição</label>
                    <input style={s.input} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div style={{ flex: "0 0 160px" }}>
                    <label style={s.label}>Público</label>
                    <select style={s.input} value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                        {Object.entries(AUDIENCE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </div>
                <button style={s.primaryBtn} onClick={save}>{form.id ? "Gravar" : "Adicionar"}</button>
                {form.id > 0 && <button style={s.chipBtn} onClick={() => setForm({ id: 0, title: "", description: "", audience: "artist" })}>Cancelar</button>}
            </div>
        </div>
    );
}

// ------------------------------------------------------------------ editor

function ArticleEditor({ initial, categories, onClose }) {
    const [a, setA] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [dirty, setDirty] = useState(false);
    const [savedOnce, setSavedOnce] = useState(false);
    const slugLocked = !!initial.published_at;
    const slugTouched = useRef(!!initial.id);

    const set = (patch) => { setA((cur) => ({ ...cur, ...patch })); setDirty(true); };
    const setStep = (i, patch) => set({ steps: a.steps.map((st, k) => (k === i ? { ...st, ...patch } : st)) });
    const moveStep = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= a.steps.length) return;
        const steps = [...a.steps];
        [steps[i], steps[j]] = [steps[j], steps[i]];
        set({ steps });
    };

    const save = async (status) => {
        setSaving(true);
        setError("");
        try {
            const payload = { ...a, status: status || a.status };
            const { data } = await izenuAPI.post("/admin/help/save.php", payload);
            const fresh = await izenuAPI.get(`/admin/help/get.php?id=${data.id}`);
            const art = fresh.data.article;
            setA({ ...art, steps: art.steps.length ? art.steps : [{ ...EMPTY_STEP }] });
            setDirty(false);
            setSavedOnce(true);
            setNotice(art.status === "published" ? "Publicado." : "Rascunho gravado.");
            setTimeout(() => setNotice(""), 2500);
        } catch (e) {
            setError(errMsg(e, "Não foi possível gravar."));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!a.id || !window.confirm(`Apagar "${a.title}"? Não pode ser desfeito.`)) return;
        await izenuAPI.post("/admin/help/delete.php", { id: a.id });
        onClose(true);
    };

    const close = () => {
        if (dirty && !window.confirm("Há alterações por gravar. Sair mesmo assim?")) return;
        onClose(savedOnce);
    };

    return (
        <div className="sm:p-6 text-left" style={{ maxWidth: 1400 }}>
            <div style={s.headerRow}>
                <div>
                    <button style={s.backBtn} onClick={close}>← Ajuda</button>
                    <h2 style={s.title}>{a.id ? a.title || "Artigo" : "Novo artigo"}</h2>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {notice && <span style={{ fontSize: 13, color: "#059669" }}>{notice}</span>}
                    <StatusBadge status={a.status} />
                    {a.id > 0 && <button style={s.dangerBtn} onClick={remove}>Apagar</button>}
                    {a.status === "published" ? (
                        <>
                            <button style={s.chipBtn} disabled={saving} onClick={() => save("draft")}>Despublicar</button>
                            <button style={s.primaryBtn} disabled={saving} onClick={() => save("published")}>{saving ? "A gravar…" : "Gravar e publicar"}</button>
                        </>
                    ) : (
                        <>
                            <button style={s.chipBtn} disabled={saving} onClick={() => save("draft")}>{saving ? "A gravar…" : "Gravar rascunho"}</button>
                            <button style={s.primaryBtn} disabled={saving} onClick={() => save("published")}>Publicar</button>
                        </>
                    )}
                </div>
            </div>

            {error && <div style={s.errorBox}>{error}</div>}

            <div style={s.split}>
                {/* ---------------- form */}
                <div style={{ flex: "1 1 520px", minWidth: 0 }}>
                    <div style={s.card}>
                        <label style={s.label}>Título</label>
                        <input style={s.input} value={a.title} placeholder="Como fazer um novo lançamento"
                            onChange={(e) => set(slugTouched.current || slugLocked ? { title: e.target.value } : { title: e.target.value, slug: slugify(e.target.value) })} />

                        <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                            <div style={{ flex: "2 1 260px" }}>
                                <label style={s.label}>Endereço</label>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <span style={{ fontSize: 12, color: "#9ca3af" }}>ajuda.izenu.ao/</span>
                                    <input style={{ ...s.input, background: slugLocked ? "#f9fafb" : "#fff" }} value={a.slug} disabled={slugLocked}
                                        onChange={(e) => { slugTouched.current = true; set({ slug: slugify(e.target.value) }); }} />
                                </div>
                                {slugLocked && <div style={s.hint}>Fixo depois de publicado, para os links partilhados continuarem a funcionar.</div>}
                            </div>
                            <div style={{ flex: "1 1 180px" }}>
                                <label style={s.label}>Categoria</label>
                                <select style={s.input} value={a.category_id} onChange={(e) => set({ category_id: Number(e.target.value) })}>
                                    <option value="">Escolher…</option>
                                    {categories.map((c) => <option key={c.id} value={c.id}>{c.title} ({AUDIENCE[c.audience]})</option>)}
                                </select>
                            </div>
                        </div>

                        <label style={{ ...s.label, marginTop: 12 }}>Resumo</label>
                        <textarea style={{ ...s.input, minHeight: 56 }} maxLength={300} value={a.summary}
                            placeholder="Uma frase: aparece na lista de artigos, na pesquisa e quando o link é partilhado."
                            onChange={(e) => set({ summary: e.target.value })} />

                        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 13, color: "#374151" }}>
                            <input type="checkbox" checked={a.featured === 1} onChange={(e) => set({ featured: e.target.checked ? 1 : 0 })} />
                            Mostrar em "Mais procurados", no topo da Ajuda
                        </label>
                    </div>

                    {a.steps.map((st, i) => (
                        <StepEditor key={i} index={i} step={st} total={a.steps.length}
                            onChange={(patch) => setStep(i, patch)}
                            onMove={(dir) => moveStep(i, dir)}
                            onRemove={() => set({ steps: a.steps.filter((_, k) => k !== i) })} />
                    ))}
                    <button style={{ ...s.chipBtn, width: "100%", padding: 12 }} onClick={() => set({ steps: [...a.steps, { ...EMPTY_STEP }] })}>
                        + Adicionar passo
                    </button>
                </div>

                {/* ---------------- preview */}
                <div style={{ flex: "1 1 420px", minWidth: 0 }}>
                    <div style={{ ...s.card, position: "sticky", top: 16, maxHeight: "calc(100vh - 32px)", overflowY: "auto" }}>
                        <div style={{ ...s.label, marginBottom: 12 }}>Pré-visualização</div>
                        <ArticlePreview article={a} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function StepEditor({ index, step, total, onChange, onMove, onRemove }) {
    const textRef = useRef(null);
    const [uploading, setUploading] = useState(null); // 'media' | 'inline'
    const [error, setError] = useState("");

    const upload = async (file) => {
        const fd = new FormData();
        fd.append("image", file);
        const { data } = await izenuAPI.post("/admin/help/uploadImage.php", fd);
        return data.url;
    };

    const pickMedia = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUploading("media");
        setError("");
        try { onChange({ media_type: "image", media_url: await upload(file) }); }
        catch (err) { setError(errMsg(err, "Falhou o envio da imagem.")); }
        finally { setUploading(null); }
    };

    // Wraps the selection (or inserts at the cursor) in the textarea.
    const wrap = (before, after = "", placeholder = "") => {
        const el = textRef.current;
        if (!el) return;
        const { selectionStart: a, selectionEnd: b, value } = el;
        const sel = value.slice(a, b) || placeholder;
        const next = value.slice(0, a) + before + sel + after + value.slice(b);
        onChange({ body: next });
        requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + before.length, a + before.length + sel.length); });
    };
    const linePrefix = (prefix) => {
        const el = textRef.current;
        if (!el) return;
        const { selectionStart: a, selectionEnd: b, value } = el;
        const start = value.lastIndexOf("\n", a - 1) + 1;
        const block = value.slice(start, b).split("\n").map((l) => prefix + l).join("\n");
        onChange({ body: value.slice(0, start) + block + value.slice(b) });
    };
    const insertImage = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUploading("inline");
        setError("");
        try { wrap(`\n![${file.name.replace(/\.[^.]+$/, "")}](${await upload(file)})\n`); }
        catch (err) { setError(errMsg(err, "Falhou o envio da imagem.")); }
        finally { setUploading(null); }
    };

    const ytInvalid = step.media_type === "youtube" && step.media_url && !youtubeId(step.media_url);

    return (
        <div style={s.card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={s.stepNum}>{index + 1}</span>
                <input style={{ ...s.input, fontWeight: 600 }} placeholder="Título do passo (ex.: Escolhe a capa)"
                    value={step.title} onChange={(e) => onChange({ title: e.target.value })} />
                <button style={s.arrow} disabled={index === 0} onClick={() => onMove(-1)} title="Subir">↑</button>
                <button style={s.arrow} disabled={index === total - 1} onClick={() => onMove(1)} title="Descer">↓</button>
                <button style={s.dangerBtn} onClick={() => { if (window.confirm(`Apagar o passo ${index + 1}?`)) onRemove(); }}>✕</button>
            </div>

            <div style={s.toolbar}>
                <button style={s.toolBtn} onClick={() => wrap("**", "**", "negrito")}><b>B</b></button>
                <button style={s.toolBtn} onClick={() => wrap("_", "_", "itálico")}><i>I</i></button>
                <button style={s.toolBtn} onClick={() => wrap("[", "](https://)", "texto do link")}>Link</button>
                <button style={s.toolBtn} onClick={() => linePrefix("- ")}>• Lista</button>
                <button style={s.toolBtn} onClick={() => linePrefix("1. ")}>1. Lista</button>
                <button style={s.toolBtn} onClick={() => linePrefix("> ")}>Nota</button>
                <label style={{ ...s.toolBtn, cursor: "pointer" }}>
                    {uploading === "inline" ? "A enviar…" : "Imagem no texto"}
                    <input type="file" accept="image/*" hidden onChange={insertImage} />
                </label>
            </div>
            <textarea ref={textRef} style={{ ...s.input, minHeight: 120, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12.5, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
                placeholder={"Explica o passo. **negrito**, _itálico_, listas com - ou 1., e > para uma nota."}
                value={step.body} onChange={(e) => onChange({ body: e.target.value })} />

            <div style={{ display: "flex", gap: 6, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ ...s.label, margin: 0 }}>Destaque do passo:</span>
                {[["none", "Nenhum"], ["image", "Captura de ecrã"], ["youtube", "Vídeo do YouTube"]].map(([k, v]) => (
                    <button key={k} style={{ ...s.chipBtn, ...(step.media_type === k ? s.chipActive : {}) }}
                        onClick={() => onChange({ media_type: k, media_url: k === step.media_type ? step.media_url : "" })}>{v}</button>
                ))}
            </div>
            {step.media_type === "image" && (
                <div style={{ marginTop: 10 }}>
                    {step.media_url && <img src={step.media_url} alt="" style={{ maxWidth: "100%", maxHeight: 180, borderRadius: 8, border: "1px solid #eee", display: "block", marginBottom: 8 }} />}
                    <label style={{ ...s.chipBtn, display: "inline-block", cursor: "pointer" }}>
                        {uploading === "media" ? "A enviar…" : step.media_url ? "Trocar imagem" : "Escolher imagem"}
                        <input type="file" accept="image/*" hidden onChange={pickMedia} />
                    </label>
                </div>
            )}
            {step.media_type === "youtube" && (
                <div style={{ marginTop: 10 }}>
                    <input style={s.input} placeholder="https://www.youtube.com/watch?v=…" value={step.media_url}
                        onChange={(e) => onChange({ media_url: e.target.value })} />
                    {ytInvalid && <div style={{ ...s.hint, color: "#b91c1c" }}>Não reconheço este link do YouTube.</div>}
                    <div style={s.hint}>Se o vídeo for "Não listado" no YouTube, só aparece aqui — não na pesquisa do YouTube.</div>
                </div>
            )}
            {error && <div style={{ ...s.errorBox, marginTop: 10, marginBottom: 0 }}>{error}</div>}
        </div>
    );
}

// Kept visually close to the site's article page.
function ArticlePreview({ article }) {
    const steps = article.steps.filter((st) => st.title || st.body || st.media_url);
    return (
        <div style={{ color: "#1f2937" }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>{article.title || "Sem título"}</h1>
            {article.summary && <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 18px" }}>{article.summary}</p>}
            {steps.length === 0 && <p style={s.emptySmall}>Sem passos ainda.</p>}
            {steps.map((st, i) => (
                <div key={i} style={{ marginBottom: 22 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
                        <span style={s.stepNum}>{i + 1}</span>
                        <h2 style={{ fontSize: 16, fontWeight: 650, margin: 0 }}>{st.title}</h2>
                    </div>
                    <div className="help-md" style={{ fontSize: 14, lineHeight: 1.6 }}>
                        <ReactMarkdown>{st.body || ""}</ReactMarkdown>
                    </div>
                    {st.media_type === "image" && st.media_url && (
                        <img src={st.media_url} alt={st.title} style={{ width: "100%", borderRadius: 10, border: "1px solid #eee", marginTop: 8 }} />
                    )}
                    {st.media_type === "youtube" && youtubeId(st.media_url) && (
                        <div style={{ position: "relative", paddingTop: "56.25%", marginTop: 8, borderRadius: 10, overflow: "hidden", background: "#000" }}>
                            <iframe title={st.title || "Vídeo"} src={`https://www.youtube-nocookie.com/embed/${youtubeId(st.media_url)}`}
                                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                                allow="accelerometer; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        </div>
                    )}
                </div>
            ))}
            <style>{`
                .help-md p { margin: 0 0 8px; }
                .help-md ul { list-style: disc; padding-left: 20px; margin: 0 0 8px; }
                .help-md ol { list-style: decimal; padding-left: 20px; margin: 0 0 8px; }
                .help-md a { color: #4f46e5; text-decoration: underline; }
                .help-md img { max-width: 100%; border-radius: 8px; border: 1px solid #eee; margin: 6px 0; }
                .help-md blockquote { border-left: 3px solid #a78bfa; background: #f5f3ff; padding: 8px 12px; margin: 8px 0; border-radius: 4px; }
                .help-md code { background: #f3f4f6; padding: 1px 5px; border-radius: 4px; font-size: 12.5px; }
                .help-md h1, .help-md h2, .help-md h3 { font-weight: 650; margin: 10px 0 6px; }
            `}</style>
        </div>
    );
}

const s = {
    container: { maxWidth: 1100 },
    headerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 20, textAlign: "left", flexWrap: "wrap" },
    title: { fontSize: 22, color: "#222", margin: 0 },
    subtitle: { margin: "8px 0 0", fontSize: 14, color: "#555", fontWeight: 400, maxWidth: 720, lineHeight: 1.5, textAlign: "left" },
    link: { color: "#4f46e5", textDecoration: "underline" },
    card: { padding: 20, backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", marginBottom: 16 },
    cardTitle: { fontSize: 15, fontWeight: 600, color: "#333", margin: 0 },
    cardDesc: { margin: "4px 0 14px", fontSize: 12, color: "#aaa", lineHeight: 1.5, maxWidth: 700 },
    label: { display: "block", fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", marginBottom: 5 },
    hint: { fontSize: 11.5, color: "#9ca3af", marginTop: 4 },
    input: { width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#1f2937", outline: "none", boxSizing: "border-box" },
    primaryBtn: { padding: "8px 14px", background: "#10b981", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" },
    chipBtn: { padding: "8px 12px", background: "#f3f4f6", color: "#374151", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500, whiteSpace: "nowrap" },
    chipActive: { background: "#4f46e5", color: "#fff" },
    chipLink: { padding: "8px 12px", background: "#eef2ff", color: "#4338ca", borderRadius: 6, fontSize: 12, fontWeight: 500, textDecoration: "none", whiteSpace: "nowrap" },
    dangerBtn: { padding: "8px 12px", background: "#fee2e2", color: "#b91c1c", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 },
    backBtn: { background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 13, padding: 0, marginBottom: 6 },
    arrow: { background: "#f3f4f6", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 11, lineHeight: 1, padding: "3px 6px", margin: 1, color: "#374151" },
    listItem: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #f5f5f5" },
    itemTitle: { fontSize: 14, fontWeight: 600, color: "#1f2937" },
    itemSub: { fontSize: 12, color: "#9ca3af", marginTop: 2 },
    badge: { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "#eef2ff", color: "#4338ca", whiteSpace: "nowrap" },
    stepNum: { flexShrink: 0, width: 26, height: 26, borderRadius: "50%", background: "#4f46e5", color: "#fff", fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" },
    toolbar: { display: "flex", gap: 4, flexWrap: "wrap", padding: 6, background: "#f9fafb", border: "1px solid #d1d5db", borderBottom: "none", borderRadius: "6px 6px 0 0" },
    toolBtn: { padding: "4px 9px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 4, fontSize: 12, color: "#374151", cursor: "pointer" },
    split: { display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" },
    empty: { color: "#ccc", textAlign: "center", padding: 24, fontSize: 13 },
    emptySmall: { color: "#bbb", fontSize: 13, margin: "6px 0" },
    errorBox: { padding: "12px 16px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 8, marginBottom: 16, fontSize: 13 },
};
